/* eslint-disable @typescript-eslint/no-explicit-any */
import { ANALYST_ACTION_FALLBACKS } from "@/constants/messages";
import { userFacingErrorMessage } from "@/utils/extract-rtk-error";
import { DivergenceBatchUpdateDto, DivergenceUpdateDto, IDivergence } from "@/@types/divergence";
import { IOneStoreConciliation } from "@/@types/store-conciliation";
import {
  FALTA_NAO_JUSTIFICADA,
  SINISTRO_FRAUDE,
} from "@/constants/justifications.id";
import { GroupsEnum } from "@/enum/groups";
import { StatusEnumStoreConciliation } from "@/enum/statusConciliation";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useUpdateDivergenceBatchMutation } from "@/services/divergence";
import { useGetJustificationActiveForDivergenceQuery } from "@/services/justification";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import useKeyboardShortcut from "@/utils/useKeyboardShortcut";
import { isUserAdminInGroup } from "@/utils/user";
import TableLoad from "components/load/TableLoad";
import { Card, CardContent, CardHeader } from "components/ui/card";
import { toast } from "components/ui/use-toast";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useCurrentConciliation } from "./ducks/filter/hooks/statusConciliation";
import { calculateTotalDivergences } from "../utils/CalculateTotalDivergences";
import { CardHeaderContent } from "./components/card-header-content";
import { Header } from "./components/header";
import HeaderFilter from "./components/header-filter";
import BrowserNotLeave from "./components/not-leave";
import { RejectedWarning } from "./components/rejected-warning";
import CreateDivergence from "./create";
import { useFilterState } from "./ducks/filter/hooks/filterState";
import { actions } from "./shortcut";
import useColumns from "./table/columns";
import { DIVERGENCE_ROW_OPEN_CASH_REGISTER_HIGHLIGHT } from "../../partial-operator-balance/types";
import { DataTable } from "./table/data-table";

interface DivergenceListProps {
  divergences: IDivergence[];
  isLoading: boolean;
  isFetching?: boolean;
  conciliation: IOneStoreConciliation | undefined;
  conciliation_id?: string;
  onRefresh?: () => void;
  openCashRegisterCodes?: readonly number[];
}

export default function DivergenceList({
  divergences,
  isLoading,
  isFetching,
  conciliation,
  conciliation_id,
  onRefresh,
  openCashRegisterCodes = [],
}: DivergenceListProps) {
  const { filters } = useFilterState() || { filters: {} };
  const { handleModal } = useDialogModal();
  const methods = useForm();
  const { control, getValues } = methods;
  const [tableKey] = useState(0);

  const { data: justifications } = useGetJustificationActiveForDivergenceQuery();

  const [updateDivergence, { isLoading: isSaveLoading }] =
    useUpdateDivergenceBatchMutation();

  const user = getUserFromLocalStorage();

  const { isClosed: isClosedByRedux } = useCurrentConciliation();

  const totalAmount = useMemo(
    () => calculateTotalDivergences(divergences),
    [divergences]
  );
  const isClosed = conciliation?.status === StatusEnumStoreConciliation.CLOSED || isClosedByRedux;
  const conciliationDate = useMemo(
    () =>
      conciliation?.date
        ? format(new Date(conciliation.date), "dd/MM/yyyy")
        : "Sem Data",
    [conciliation?.date]
  );
  const processingDate = useMemo(
    () =>
      conciliation?.last_processing
        ? format(new Date(conciliation.last_processing), "dd/MM/yyyy HH:mm")
        : "Sem Data",
    [conciliation?.last_processing]
  );
  const codeTitle = conciliation?.store?.code || "0";

  // Filtrar divergências rejeitadas para o warning
  const rejectedDivergences = useMemo(
    () =>
      divergences
        .filter((divergence) => String(divergence.status).includes("REJECTED"))
        .map((divergence) => ({
          id: Number(divergence.id),
          name: divergence.name,
          amount: divergence.amount,
        })),
    [divergences]
  );

  // Check if user is admin in CONCILIACAO module
  const isAdmin = isUserAdminInGroup(user, GroupsEnum.CONCILIACAO);

  const columns = useColumns({ control, methods, totalAmount, isClosed });

  // Memorize actions array to avoid repeated calls
  const actionsList = useMemo(
    () => actions(handleModal, codeTitle, divergences ?? [], conciliation_id as string, isClosed),
    [handleModal, codeTitle, divergences, conciliation_id, isClosed]
  );

  // Shortcuts
  actionsList.forEach((action: any) => {
    useKeyboardShortcut(action.shortcut, action.onclick, true);
  });

  const handleAddDivergence = useCallback(() => {
    handleModal({
      isOpen: true,
      element: <CreateDivergence id_transaction={conciliation_id as string} />,
      title: "Adicionar divergência",
    });
  }, [handleModal, conciliation_id]);

  const handleSaveAll = useCallback(async () => {
    try {
      const formData = getValues("divergence");
      if (!formData || typeof formData !== "object") {
        throw new Error("Invalid divergences data");
      }

      const divergences: DivergenceUpdateDto[] = Object.keys(formData)
        .map((key) => {
          const idMatch = key.match(/id_(\d+)/);
          if (!idMatch) return null;

          const divergenceId = idMatch[1];
          const justificationId = +formData[`justification_${divergenceId}`];
          let isLoss = formData[`is_loss_${divergenceId}`];

          if (
            justificationId === SINISTRO_FRAUDE ||
            justificationId === FALTA_NAO_JUSTIFICADA
          ) {
            isLoss = true;
          }

          const divergence: DivergenceUpdateDto = {
            id: +formData[`id_${divergenceId}`],
            version: +formData[`version_${divergenceId}`],
            justification_date: formData[`justification_date_${divergenceId}`] || undefined,
            is_loss: isLoss,
            has_evidence: formData[`has_evidence_${divergenceId}`],
            has_penalty: formData[`has_penalty_${divergenceId}`] || false,
            justification_id: justificationId,
          };

          return divergence;
        })
        .filter((item) => item !== null) as DivergenceUpdateDto[];

      const batchData: DivergenceBatchUpdateDto = {
        conciliation_id: +(conciliation_id as string),
        divergences,
      };

      await updateDivergence(batchData).unwrap();

      toast({
        variant: "default",
        description: "Itens salvos com sucesso!",
        duration: 5000,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        description: userFacingErrorMessage(
          error,
          ANALYST_ACTION_FALLBACKS.BATCH_FAILED
        ),
        duration: 5000,
      });
    }
  }, [getValues, conciliation_id, updateDivergence]);

  useKeyboardShortcut(
    "Enter",
    () => {
      if (!isClosed) {
        handleSaveAll();
      }
    },
    true
  );

  return (
    <div className="relative w-full min-w-0 overflow-hidden">
      <BrowserNotLeave />
      <Header
        codeTitle={codeTitle}
        conciliationDate={conciliationDate}
        isClosed={isClosed}
        isAdmin={isAdmin}
        isLoading={isLoading}
        isSaveLoading={isSaveLoading}
        onSaveAll={handleSaveAll}
      />
      <Card className="w-full shadow-none">
        <CardHeader>
          <CardHeaderContent
            conciliation={conciliation}
            processingDate={processingDate}
            totalAmount={totalAmount}
            actions={actionsList}
            isClosed={isClosed}
            isAdmin={isAdmin}
            isLoading={isLoading}
            isFetching={isFetching}
            isSaveLoading={isSaveLoading}
            onAddDivergence={handleAddDivergence}
            onSaveAll={handleSaveAll}
            onRefresh={onRefresh}
            conciliation_id={conciliation_id}
          />
        </CardHeader>

        <CardContent className="relative shadow-none">
          {isSaveLoading && <TableLoad />}
          <RejectedWarning rejectedDivergences={rejectedDivergences} />
          <div className="flex flex-col md:flex-row gap-4">
            <HeaderFilter
              hookName="popoverFilterDivergence"
              popoverState={filters?.popoverFilterDivergence}
              data={divergences}
              hookFilterSelected="divergence"
            />
            <HeaderFilter
              hookName="popoverFilterJustification"
              data={justifications ?? []}
              popoverState={filters?.popoverFilterJustification}
              hookFilterSelected="justification"
            />
          </div>
          <div className="overflow-x-auto">
            <DataTable
              key={tableKey}
              data={divergences}
              columns={columns}
              isLoading={isLoading}
              rowClassName={(divergence) =>
                openCashRegisterCodes.includes(Number(divergence.register_code))
                  ? DIVERGENCE_ROW_OPEN_CASH_REGISTER_HIGHLIGHT
                  : ""
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
